import type { SupportCenterAgendaEvent } from '../../types/supportCenterAppointments'
import {
  isSupportCenterFormKind,
  SUPPORT_CENTER_FORM_KIND_LABELS,
  type SupportCenterFormKind,
} from '../../types/supportCenterSpecialties'
import { APPOINTMENT_STATUS_LABELS } from '../../types/supportCenterReports'
import {
  TRIAGE_STATUS_LABELS,
  type SupportCenterAppointmentStatus,
  type SupportCenterTriageStatus,
} from '../../types/supportCenterTriage'
import type { SlotOccupancyMap } from './agendaOccupancyHelpers'
import { onlyDigits } from './dashboardHelpers'

export const CALENDARIO_MODULE_NAME = 'central_amparo_calendario'

export const DEFAULT_AGENDA_HOUR_START = 7
export const DEFAULT_AGENDA_HOUR_END = 20

export const DEFAULT_STATUS_FILTER: SupportCenterAppointmentStatus[] = [
  'marcado',
  'reagendado',
  'realizado',
  'nao_realizado',
]

export const STATUS_FILTER_OPTIONS = (
  Object.keys(APPOINTMENT_STATUS_LABELS) as SupportCenterAppointmentStatus[]
).map((status) => ({
  value: status,
  label: APPOINTMENT_STATUS_LABELS[status],
}))

export type AgendaTriageStatusFilter = SupportCenterTriageStatus | 'none'

export const TRIAGE_STATUS_FILTER_OPTIONS = [
  { value: 'none' as const, label: 'Sem triagem' },
  ...(Object.keys(TRIAGE_STATUS_LABELS) as SupportCenterTriageStatus[]).map((status) => ({
    value: status,
    label: TRIAGE_STATUS_LABELS[status],
  })),
]

export const DEFAULT_TRIAGE_STATUS_FILTER: AgendaTriageStatusFilter[] =
  TRIAGE_STATUS_FILTER_OPTIONS.map((option) => option.value)

export function matchesAgendaPatientQuery(
  event: SupportCenterAgendaEvent,
  query: string
): boolean {
  const needle = query.trim()
  if (!needle) return true
  const digits = onlyDigits(needle)
  const nameNeedle = needle.toLowerCase()
  const cpfMatch =
    digits.length >= 3 &&
    (onlyDigits(event.patient_cpf).includes(digits) ||
      onlyDigits(event.holder_cpf).includes(digits))
  const nameMatch =
    (event.patient_people?.name ?? '').toLowerCase().includes(nameNeedle) ||
    (event.holder_people?.name ?? '').toLowerCase().includes(nameNeedle)
  return cpfMatch || nameMatch
}

export function matchesAgendaTriageFilter(
  event: SupportCenterAgendaEvent,
  filter: AgendaTriageStatusFilter[]
): boolean {
  if (filter.length === 0) return false
  if (!event.triage_status) return filter.includes('none')
  return filter.includes(event.triage_status)
}

/**
 * true se a hora local do agendamento já terminou
 * (início da hora agendada estritamente anterior ao início da hora de `now`).
 * Data inválida → false (mantém visível, como o filtro atual).
 */
export function isAgendaEventBeforeCurrentHour(
  scheduledAt: string,
  now: Date = new Date()
): boolean {
  const when = new Date(
    scheduledAt.includes('T') ? scheduledAt : scheduledAt.replace(' ', 'T')
  )
  if (Number.isNaN(when.getTime())) return false
  const eventHourStart = new Date(when)
  eventHourStart.setMinutes(0, 0, 0)
  const currentHourStart = new Date(now)
  currentHourStart.setMinutes(0, 0, 0)
  return eventHourStart.getTime() < currentHourStart.getTime()
}

export function resolveLoggedEmail(
  user: { email?: string | null } | null,
  profile: { email?: string | null } | null
): string {
  const fromUser = typeof user?.email === 'string' ? user.email.trim() : ''
  if (fromUser) return fromUser
  const fromProfile = typeof profile?.email === 'string' ? profile.email.trim() : ''
  return fromProfile
}

/** Faixa vertical da grade: min–max dos arrays de horas; vazio → 7–20. */
export function resolveAgendaHourRange(hoursLists: number[][]): {
  start: number
  end: number
} {
  const hours: number[] = []
  for (const list of hoursLists) {
    for (const raw of list) {
      const n = Number(raw)
      if (!Number.isInteger(n) || n < 0 || n > 23) continue
      hours.push(n)
    }
  }
  if (hours.length === 0) {
    return { start: DEFAULT_AGENDA_HOUR_START, end: DEFAULT_AGENDA_HOUR_END }
  }
  return { start: Math.min(...hours), end: Math.max(...hours) }
}

const FORM_KIND_SWATCH: Record<SupportCenterFormKind | 'default', string> = {
  psicologico: 'bg-violet-600',
  nutricional: 'bg-amber-600',
  fitness: 'bg-sky-600',
  juridico: 'bg-emerald-600',
  social: 'bg-rose-600',
  default: 'bg-primary-600',
}

const FORM_KIND_ACCENT: Record<SupportCenterFormKind | 'default', string> = {
  psicologico: 'border-l-violet-500',
  nutricional: 'border-l-amber-500',
  fitness: 'border-l-sky-500',
  juridico: 'border-l-emerald-500',
  social: 'border-l-rose-500',
  default: 'border-l-primary-500',
}

const FORM_KIND_CARD_BG: Record<SupportCenterFormKind | 'default', string> = {
  psicologico:
    'bg-violet-600/10 hover:bg-violet-600/20 dark:bg-violet-500/20 dark:hover:bg-violet-500/30',
  nutricional:
    'bg-amber-600/10 hover:bg-amber-600/20 dark:bg-amber-500/20 dark:hover:bg-amber-500/30',
  fitness: 'bg-sky-600/10 hover:bg-sky-600/20 dark:bg-sky-500/20 dark:hover:bg-sky-500/30',
  juridico:
    'bg-emerald-600/10 hover:bg-emerald-600/20 dark:bg-emerald-500/20 dark:hover:bg-emerald-500/30',
  social:
    'bg-rose-600/10 hover:bg-rose-600/20 dark:bg-rose-500/20 dark:hover:bg-rose-500/30',
  default:
    'bg-primary-600/10 hover:bg-primary-600/20 dark:bg-primary-500/20 dark:hover:bg-primary-500/30',
}

export type AgendaEventColorLegendItem = {
  key: string
  label: string
  swatchClass: string
}

export const PROFESSIONAL_COLOR_LEGEND: AgendaEventColorLegendItem[] = (
  Object.keys(SUPPORT_CENTER_FORM_KIND_LABELS) as SupportCenterFormKind[]
).map((kind) => ({
  key: kind,
  label: SUPPORT_CENTER_FORM_KIND_LABELS[kind],
  swatchClass: FORM_KIND_SWATCH[kind],
}))

function formKindKey(formKind: string | null | undefined): SupportCenterFormKind | 'default' {
  return isSupportCenterFormKind(formKind) ? formKind : 'default'
}

export function agendaEventCardClass(event: SupportCenterAgendaEvent): string {
  const key = formKindKey(event.assistance?.form_kind)
  return `${FORM_KIND_CARD_BG[key]} ring-1 ring-black/10 dark:ring-white/10 ${FORM_KIND_ACCENT[key]}`
}

export function agendaEventSwatchClass(event: SupportCenterAgendaEvent): string {
  return FORM_KIND_SWATCH[formKindKey(event.assistance?.form_kind)]
}

export function agendaEventColorLabel(event: SupportCenterAgendaEvent): string {
  const formKind = isSupportCenterFormKind(event.assistance?.form_kind)
    ? event.assistance.form_kind
    : null
  return (
    event.specialization?.name ||
    (formKind ? SUPPORT_CENTER_FORM_KIND_LABELS[formKind] : 'Especialidade')
  )
}

export function agendaEventColorLegendItems(): AgendaEventColorLegendItem[] {
  return PROFESSIONAL_COLOR_LEGEND
}

/** Badges Tr./Ag. nos cards da grade — fundos mais saturados que as listas. */
export function agendaEventTriageBadgeClass(
  status: SupportCenterTriageStatus | null | undefined
): string {
  if (!status) {
    return 'bg-gray-300 text-gray-900 dark:bg-gray-500 dark:text-white'
  }
  if (status === 'pendente') {
    return 'bg-amber-300 text-amber-950 dark:bg-amber-500 dark:text-amber-950'
  }
  if (status === 'concluida') {
    return 'bg-emerald-300 text-emerald-950 dark:bg-emerald-500 dark:text-emerald-950'
  }
  if (status === 'nao_realizado') {
    return 'bg-slate-300 text-slate-900 dark:bg-slate-500 dark:text-white'
  }
  return 'bg-red-300 text-red-950 dark:bg-red-500 dark:text-white'
}

export function agendaEventAppointmentBadgeClass(
  status: SupportCenterAppointmentStatus
): string {
  if (status === 'marcado') {
    return 'bg-sky-300 text-sky-950 dark:bg-sky-500 dark:text-sky-950'
  }
  if (status === 'reagendado') {
    return 'bg-amber-300 text-amber-950 dark:bg-amber-500 dark:text-amber-950'
  }
  if (status === 'cancelado') {
    return 'bg-red-300 text-red-950 dark:bg-red-500 dark:text-white'
  }
  if (status === 'realizado') {
    return 'bg-emerald-300 text-emerald-950 dark:bg-emerald-500 dark:text-emerald-950'
  }
  return 'bg-slate-300 text-slate-900 dark:bg-slate-500 dark:text-white'
}

export type AgendaStatusSummary = {
  byStatus: Record<SupportCenterAppointmentStatus, number>
  total: number
}

export function summarizeAgendaStatuses(
  events: SupportCenterAgendaEvent[]
): AgendaStatusSummary {
  const statuses = Object.keys(APPOINTMENT_STATUS_LABELS) as SupportCenterAppointmentStatus[]
  const byStatus = Object.fromEntries(statuses.map((status) => [status, 0])) as Record<
    SupportCenterAppointmentStatus,
    number
  >
  for (const event of events) {
    if (event.status in byStatus) {
      byStatus[event.status] += 1
    }
  }
  return { byStatus, total: events.length }
}

export type SlotOccupancySummary = {
  occupied: number
  capacity: number
  rate: number | null
}

export function summarizeSlotOccupancy(
  map: SlotOccupancyMap | null
): SlotOccupancySummary {
  if (!map || map.size === 0) {
    return { occupied: 0, capacity: 0, rate: null }
  }
  let capacity = 0
  let free = 0
  for (const slot of map.values()) {
    capacity += slot.total
    free += slot.free
  }
  const occupied = Math.max(0, capacity - free)
  return {
    occupied,
    capacity,
    rate: capacity > 0 ? occupied / capacity : null,
  }
}

/** Ações do painel Detalhe — sólido (CTA), outline (secundário), danger (destrutivo). */
export const DETAIL_ACTION_SOLID_CLASS =
  'w-full !bg-primary-600 !text-white hover:!bg-primary-700 dark:!bg-primary-600 dark:hover:!bg-primary-500 !ring-primary-600'

export const DETAIL_ACTION_OUTLINE_CLASS =
  'w-full !text-primary-800 hover:!bg-primary-50 dark:!text-primary-200 dark:hover:!bg-primary-950/40 !ring-primary-300 hover:!ring-primary-400 dark:!ring-primary-700/70 dark:hover:!ring-primary-600/80'

/** Grade "Ações": cada botão com borda/texto na própria cor. */
export const DETAIL_ACTION_RESCHEDULE_CLASS =
  'w-full !text-amber-700 hover:!bg-amber-50 dark:!text-amber-300 dark:hover:!bg-amber-950/40 !ring-amber-400 hover:!ring-amber-500 dark:!ring-amber-700/70 dark:hover:!ring-amber-600/80'

export const DETAIL_ACTION_LINK_CLASS =
  'w-full !text-sky-700 hover:!bg-sky-50 dark:!text-sky-300 dark:hover:!bg-sky-950/40 !ring-sky-400 hover:!ring-sky-500 dark:!ring-sky-700/70 dark:hover:!ring-sky-600/80'

export const DETAIL_ACTION_HISTORY_CLASS =
  'w-full !text-slate-700 hover:!bg-slate-50 dark:!text-slate-300 dark:hover:!bg-slate-800/50 !ring-slate-400 hover:!ring-slate-500 dark:!ring-slate-600 dark:hover:!ring-slate-500'

export const DETAIL_ACTION_DANGER_CLASS =
  'w-full !text-red-600 hover:!bg-red-50 dark:!text-red-400 dark:hover:!bg-red-950/40 !ring-red-400 hover:!ring-red-500 dark:!ring-red-900/60 dark:hover:!ring-red-700/80'
